import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  TrendingDown, TrendingUp, AlertCircle, Bell, Download, FileText,
  Sparkles, RefreshCw, Trophy, Package, LineChart as LineIcon,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";
import ItemIntelligencePanel from "./procurement/ItemIntelligencePanel";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const REPORTS = [
  { kind: "po-comparison", label: "PO Comparison" },
  { kind: "savings", label: "Savings Opportunities" },
  { kind: "supplier-performance", label: "Supplier Performance" },
  { kind: "monthly", label: "Monthly Intelligence" },
  { kind: "category", label: "Category Savings" },
  { kind: "price-trend", label: "Price Trend Analysis" },
];

const ALERT_STYLES = {
  critical: "border-[hsl(6_62%_45%)] bg-[hsl(6_60%_97%)] text-[hsl(6_62%_45%)]",
  warning: "border-[hsl(35_75%_38%)] bg-[hsl(38_90%_95%)] text-[hsl(35_75%_38%)]",
  info: "border-[hsl(var(--forest))]/40 bg-[hsl(148_40%_95%)] text-[hsl(var(--forest))]",
};

export default function ProcurementIntelligence() {
  const [months, setMonths] = useState(12);
  const [tab, setTab] = useState("overview"); // overview | trends | items | vendors | alerts | reports
  const [kpis, setKpis] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [topOverpriced, setTopOverpriced] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);
  const [vendorRows, setVendorRows] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [permissions, setPermissions] = useState({ capabilities: {} });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [itemPanel, setItemPanel] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [k, m, tp, c, v, a, p] = await Promise.all([
        api.get("/staff/procurement/intelligence/kpis", { params: { months } }),
        api.get("/staff/procurement/intelligence/monthly-savings", { params: { months } }),
        api.get("/staff/procurement/intelligence/top-overpriced", { params: { months, limit: 20 } }),
        api.get("/staff/procurement/intelligence/category-breakdown", { params: { months } }),
        api.get("/staff/procurement/intelligence/vendor-comparison", { params: { months } }),
        api.get("/staff/procurement/intelligence/alerts"),
        api.get("/staff/procurement/intelligence/my-permissions"),
      ]);
      setKpis(k.data);
      setMonthly(m.data.months || []);
      setTopOverpriced(tp.data.rows || []);
      setCategoryRows(c.data.rows || []);
      setVendorRows(v.data.rows || []);
      setAlerts(a.data.alerts || []);
      setPermissions(p.data);
    } catch (e) { toast.error("Failed to load procurement intelligence"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, [months]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportReport = async (kind, ext) => {
    setExporting(`${kind}.${ext}`);
    try {
      const { data } = await api.get(`/staff/procurement/intelligence/reports/${kind}.${ext}`, {
        params: { months }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `tbb-${kind}.${ext}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setExporting(null); }
  };

  const k = kpis?.kpis || {};
  const canExport = !!permissions?.capabilities?.export_reports;

  return (
    <section data-testid="procurement-intelligence" className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-8">
      {itemPanel && (
        <ItemIntelligencePanel
          itemName={itemPanel.item_name}
          itemId={itemPanel.item_id}
          unit={itemPanel.unit}
          onClose={() => setItemPanel(null)}
        />
      )}

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><LineIcon size={12} /> Procurement</div>
          <h1 className="display text-3xl mt-1">Intelligence dashboard</h1>
          <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">
            Consolidated benchmark comparison across every purchase order in the last {months} months.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="pi-window"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="text-xs px-3 py-2 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]"
          >
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
          <button
            data-testid="pi-refresh"
            onClick={loadAll}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg-2))]"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </header>

      {loading && !kpis ? (
        <div className="text-sm text-[hsl(var(--ink-soft))]">Loading…</div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--rule))] border border-[hsl(var(--rule))]">
            <Kpi label="Procurement value" value={inr(k.total_po_value)} sub={`${k.pos_analysed || 0} POs · ${k.items_analysed || 0} items`} testid="kpi-po-value" />
            <Kpi label="Benchmark equivalent" value={inr(k.benchmark_equivalent_value)} sub={`Coverage ${k.benchmark_coverage_pct}%`} testid="kpi-bench-value" />
            <Kpi label="Total savings opportunity" value={inr(k.total_savings_opportunity)} sub={`${k.savings_pct}%`} tone={k.total_savings_opportunity > 0 ? "danger" : "forest"} testid="kpi-savings" />
            <Kpi label="This month savings" value={inr(k.this_month_savings)} sub="Since 1st" tone={k.this_month_savings > 0 ? "danger" : "forest"} testid="kpi-month-savings" />
            <Kpi label="Supplier competitiveness" value={`${k.supplier_competitiveness_score ?? 0}`} sub="0–100 · higher = better" tone={k.supplier_competitiveness_score >= 90 ? "forest" : k.supplier_competitiveness_score >= 70 ? "brass" : "danger"} testid="kpi-competitiveness" />
            <Kpi label="Average price diff" value={inr2(k.avg_price_diff)} sub="Per matched line" testid="kpi-avg-diff" />
            <Kpi label="Price variance" value={`${k.price_variance_pct ?? 0}%`} sub="Avg |diff| / supplier" testid="kpi-variance" />
            <Kpi label="Benchmark coverage" value={`${k.benchmark_coverage_pct ?? 0}%`} sub={`${(k.items_analysed || 0) - Math.round((k.items_analysed || 0) * (100 - (k.benchmark_coverage_pct || 0)) / 100)}/${k.items_analysed || 0} matched`} tone={k.benchmark_coverage_pct === 100 ? "forest" : k.benchmark_coverage_pct >= 50 ? "brass" : "danger"} testid="kpi-coverage" />
          </div>

          {/* Tab strip */}
          <div className="border-b border-[hsl(var(--rule))]">
            <nav className="flex flex-wrap gap-1">
              {[
                { key: "overview", label: "Overview" },
                { key: "trends", label: "Trends" },
                { key: "items", label: "Top items" },
                { key: "vendors", label: "Vendors" },
                { key: "alerts", label: `Alerts${alerts.length ? ` (${alerts.length})` : ""}`, icon: Bell },
                { key: "reports", label: "Reports", icon: Download, disabled: !canExport },
              ].map((t) => (
                <button
                  key={t.key}
                  data-testid={`pi-tab-${t.key}`}
                  onClick={() => !t.disabled && setTab(t.key)}
                  disabled={t.disabled}
                  title={t.disabled ? "Requires manager or admin role" : undefined}
                  className={`px-3 py-2 text-xs uppercase tracking-wider border-b-2 -mb-px ${tab === t.key ? "border-[hsl(var(--forest))] text-[hsl(var(--forest))]" : "border-transparent text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"} ${t.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {t.icon ? <t.icon size={10} className="inline mr-1" /> : null}
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          {tab === "overview" && (
            <OverviewTab
              kpis={kpis}
              monthly={monthly}
              categoryRows={categoryRows}
              onOpenItem={(it) => setItemPanel(it)}
              topOverpriced={topOverpriced}
            />
          )}
          {tab === "trends" && <TrendsTab monthly={monthly} categoryRows={categoryRows} />}
          {tab === "items" && <ItemsTab rows={topOverpriced} onOpenItem={(it) => setItemPanel(it)} />}
          {tab === "vendors" && <VendorsTab rows={vendorRows} kpiSuppliers={kpis?.top_suppliers || []} />}
          {tab === "alerts" && <AlertsTab alerts={alerts} onOpenItem={(name, unit) => setItemPanel({ item_name: name, unit })} />}
          {tab === "reports" && <ReportsTab canExport={canExport} exporting={exporting} onExport={exportReport} />}
        </>
      )}
    </section>
  );
}

// ------------------------------------------------------------------
function Kpi({ label, value, sub, tone, testid }) {
  const t = typeof tone === "function" ? tone() : tone;
  const cls = t === "danger" ? "text-[hsl(6_62%_45%)]" : t === "forest" ? "text-[hsl(var(--forest))]" : t === "brass" ? "text-[hsl(var(--brass-2))]" : "text-[hsl(var(--ink))]";
  return (
    <div data-testid={testid} className="p-3 bg-[hsl(var(--bg))]">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-1 display text-xl ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-[hsl(var(--ink-soft))] mt-0.5">{sub}</div>}
    </div>
  );
}

// ------------------------------------------------------------------
function OverviewTab({ monthly, categoryRows, onOpenItem, topOverpriced }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard title="Monthly savings trend" testid="chart-monthly">
        {monthly.length === 0 ? <Empty msg="No POs with benchmarks in this window." /> : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monthly} margin={{ top: 10, right: 12, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--rule))" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" />
              <RTooltip contentStyle={{ fontSize: 11, backgroundColor: "hsl(var(--bg))", border: "1px solid hsl(var(--rule))" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="po_value" name="PO value" stroke="hsl(var(--ink))" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="benchmark_value" name="Benchmark equiv." stroke="#2E5C3E" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="savings" name="Savings opp." stroke="#B4302E" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Procurement spend by category" testid="chart-category">
        {categoryRows.length === 0 ? <Empty msg="No category data yet." /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={categoryRows.slice(0, 10)} margin={{ top: 10, right: 12, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--rule))" />
              <XAxis dataKey="category" tick={{ fontSize: 9 }} stroke="hsl(var(--ink-soft))" interval={0} angle={-20} height={50} />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" />
              <RTooltip contentStyle={{ fontSize: 11, backgroundColor: "hsl(var(--bg))", border: "1px solid hsl(var(--rule))" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="po_value" name="Spend" fill="hsl(var(--ink))" />
              <Bar dataKey="savings" name="Savings opp." fill="#B4302E" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Top 20 overpriced items" testid="chart-overpriced" className="lg:col-span-2">
        {topOverpriced.length === 0 ? <Empty msg="No overpriced items in this window." /> : (
          <div className="max-h-80 overflow-y-auto border border-[hsl(var(--rule))]">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left p-2">Item</th>
                  <th className="text-right p-2">Total savings</th>
                  <th className="text-right p-2">Avg diff / {topOverpriced[0]?.unit || "unit"}</th>
                  <th className="text-right p-2">POs</th>
                  <th className="text-left p-2">Best vendor</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--rule))]">
                {topOverpriced.map((r, i) => (
                  <tr key={i} className="hover:bg-[hsl(var(--bg-2))]">
                    <td className="p-2 font-medium">{r.item_name}<span className="ml-1 text-[10px] text-[hsl(var(--ink-soft))]">({r.unit})</span></td>
                    <td className="p-2 text-right font-mono text-[hsl(6_62%_45%)]">{inr(r.total_savings)}</td>
                    <td className="p-2 text-right font-mono">{inr2(r.avg_diff)}</td>
                    <td className="p-2 text-right font-mono">{r.po_count}</td>
                    <td className="p-2">{r.best_vendor_name}</td>
                    <td className="p-2 text-right">
                      <button
                        data-testid={`pi-open-item-${i}`}
                        onClick={() => onOpenItem({ item_name: r.item_name, unit: r.unit })}
                        className="text-[10px] px-2 py-1 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>
    </div>
  );
}

function TrendsTab({ monthly, categoryRows }) {
  return (
    <div className="space-y-6">
      <ChartCard title="Monthly savings vs PO value" testid="chart-monthly-full">
        {monthly.length === 0 ? <Empty msg="No POs with benchmarks." /> : (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={monthly} margin={{ top: 20, right: 30, bottom: 20, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--rule))" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--ink-soft))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--ink-soft))" />
              <RTooltip contentStyle={{ fontSize: 11, backgroundColor: "hsl(var(--bg))", border: "1px solid hsl(var(--rule))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="po_value" name="PO value" stroke="hsl(var(--ink))" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="benchmark_value" name="Benchmark equiv." stroke="#2E5C3E" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="savings" name="Savings opp." stroke="#B4302E" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Category breakdown" testid="chart-category-full">
        <div className="border border-[hsl(var(--rule))]">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
              <tr>
                <th className="text-left p-2">Category</th>
                <th className="text-right p-2">Spend</th>
                <th className="text-right p-2">Savings opp.</th>
                <th className="text-right p-2">Savings %</th>
                <th className="text-right p-2">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--rule))]">
              {categoryRows.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-[hsl(var(--ink-soft))]">No data.</td></tr>
              ) : categoryRows.map((r) => (
                <tr key={r.category}>
                  <td className="p-2 font-medium">{r.category}</td>
                  <td className="p-2 text-right font-mono">{inr(r.po_value)}</td>
                  <td className="p-2 text-right font-mono text-[hsl(6_62%_45%)]">{inr(r.savings)}</td>
                  <td className="p-2 text-right font-mono">{r.savings_pct}%</td>
                  <td className="p-2 text-right font-mono">{r.coverage_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

function ItemsTab({ rows, onOpenItem }) {
  if (!rows.length) return <Empty msg="No overpriced items in this window." />;
  return (
    <div className="border border-[hsl(var(--rule))]">
      <table className="w-full text-sm" data-testid="pi-items-table">
        <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider text-xs">
          <tr>
            <th className="text-left p-2">#</th>
            <th className="text-left p-2">Item</th>
            <th className="text-right p-2">Total savings</th>
            <th className="text-right p-2">Avg diff</th>
            <th className="text-right p-2">Total qty</th>
            <th className="text-right p-2">POs</th>
            <th className="text-left p-2">Cheapest vendor</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-[hsl(var(--rule))]">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-[hsl(var(--bg-2))]">
              <td className="p-2 text-[hsl(var(--ink-soft))]">{i + 1}</td>
              <td className="p-2 font-medium">{r.item_name}<span className="ml-1 text-[10px] text-[hsl(var(--ink-soft))]">({r.unit})</span></td>
              <td className="p-2 text-right font-mono text-[hsl(6_62%_45%)] font-semibold">{inr(r.total_savings)}</td>
              <td className="p-2 text-right font-mono">{inr2(r.avg_diff)}</td>
              <td className="p-2 text-right font-mono">{r.total_qty}</td>
              <td className="p-2 text-right font-mono">{r.po_count}</td>
              <td className="p-2">{r.best_vendor_name}</td>
              <td className="p-2 text-right">
                <button
                  data-testid={`pi-item-details-${i}`}
                  onClick={() => onOpenItem({ item_name: r.item_name, unit: r.unit })}
                  className="text-xs px-2 py-1 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))]"
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VendorsTab({ rows, kpiSuppliers }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard title="Benchmark vendor coverage" testid="chart-vendor-comparison">
        {rows.length === 0 ? <Empty msg="No benchmark vendor data." /> : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows} margin={{ top: 10, right: 12, bottom: 20, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--rule))" />
              <XAxis dataKey="vendor_name" tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" angle={-20} height={60} interval={0} />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--ink-soft))" />
              <RTooltip contentStyle={{ fontSize: 11, backgroundColor: "hsl(var(--bg))", border: "1px solid hsl(var(--rule))" }} />
              <Bar dataKey="coverage_pct" name="Coverage %">
                {rows.map((r, i) => (
                  <Cell key={i} fill={r.coverage_pct >= 80 ? "#2E5C3E" : r.coverage_pct >= 40 ? "#B8862B" : "#B4302E"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        <div className="mt-2 text-[10px] text-[hsl(var(--ink-soft))]">Coverage % = benchmark rows we have for the items you actually buy.</div>
      </ChartCard>

      <ChartCard title="Supplier competitiveness (higher = better)" testid="chart-suppliers">
        {kpiSuppliers.length === 0 ? <Empty msg="No supplier data." /> : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
            {kpiSuppliers.map((s) => (
              <div key={s.vendor_id} className="p-2 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]" data-testid={`supplier-row-${s.vendor_id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.vendor_name || "—"}</div>
                    <div className="text-[10px] text-[hsl(var(--ink-soft))]">{s.po_count} PO{s.po_count === 1 ? "" : "s"} · {inr(s.po_value)} spend · {inr(s.potential_savings)} savings opp.</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold">{s.competitiveness}</div>
                    <div className="text-[9px] text-[hsl(var(--ink-soft))]">savings {s.savings_pct}%</div>
                  </div>
                </div>
                <div className="mt-1.5 h-1.5 bg-[hsl(var(--rule))] rounded overflow-hidden">
                  <div className={`h-full ${s.competitiveness >= 90 ? "bg-[hsl(var(--forest))]" : s.competitiveness >= 70 ? "bg-[hsl(var(--brass-2))]" : "bg-[hsl(6_62%_45%)]"}`} style={{ width: `${s.competitiveness}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </ChartCard>

      <ChartCard title="Total value if sourced from each benchmark" testid="chart-vendor-value" className="lg:col-span-2">
        {rows.length === 0 ? <Empty msg="No vendor data." /> : (
          <div className="border border-[hsl(var(--rule))]">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2">Vendor</th>
                  <th className="text-right p-2">Items covered</th>
                  <th className="text-right p-2">Coverage %</th>
                  <th className="text-right p-2">Value if sourced</th>
                  <th className="text-right p-2">vs actual</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--rule))]">
                {rows.map((r) => (
                  <tr key={r.vendor_id} data-testid={`vendor-row-${r.vendor_id}`}>
                    <td className="p-2 font-medium">{r.vendor_name}<span className="ml-1 text-[9px] text-[hsl(var(--ink-soft))]">P{r.priority}</span></td>
                    <td className="p-2 text-right font-mono">{r.items_covered}/{r.items_total}</td>
                    <td className="p-2 text-right font-mono">{r.coverage_pct}%</td>
                    <td className="p-2 text-right font-mono">{r.items_covered > 0 ? inr(r.total_value_if_sourced) : "—"}</td>
                    <td className={`p-2 text-right font-mono ${r.vs_actual_pct > 0 ? "text-[hsl(var(--forest))]" : r.vs_actual_pct < 0 ? "text-[hsl(6_62%_45%)]" : ""}`}>
                      {r.vs_actual_pct != null ? `${r.vs_actual_pct > 0 ? "-" : "+"}${Math.abs(r.vs_actual_pct)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2 text-[10px] text-[hsl(var(--ink-soft))]">vs actual: negative % = this vendor would have cost more; positive % = savings vs current supplier spend.</div>
      </ChartCard>
    </div>
  );
}

function AlertsTab({ alerts, onOpenItem }) {
  if (!alerts.length) return <Empty msg="No alerts — everything looks calm." />;
  return (
    <div data-testid="pi-alerts-list" className="space-y-2">
      {alerts.map((a, i) => (
        <div key={i} className={`border p-3 flex items-start gap-3 ${ALERT_STYLES[a.severity] || ALERT_STYLES.info}`} data-testid={`pi-alert-${i}`}>
          <Bell size={14} className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{a.kind.replaceAll("_", " ")}</div>
            <div className="text-sm mt-0.5">{a.message}</div>
            {(a.item_name || a.vendor_name) && (
              <div className="text-[10px] mt-1 opacity-70">
                {a.item_name && <span>Item: <b>{a.item_name}</b>{" · "}</span>}
                {a.vendor_name && <span>Vendor: <b>{a.vendor_name}</b></span>}
                {a.date && <span>{" · "}Date: {a.date}</span>}
                {a.po_number && <span>{" · "}PO: {a.po_number}</span>}
              </div>
            )}
          </div>
          {a.item_name && (
            <button
              data-testid={`pi-alert-open-${i}`}
              onClick={() => onOpenItem(a.item_name, "kg")}
              className="text-[10px] px-2 py-1 border border-current hover:bg-current/10 self-center whitespace-nowrap"
            >
              Item detail
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ReportsTab({ canExport, exporting, onExport }) {
  if (!canExport) {
    return (
      <div className="p-6 text-sm text-[hsl(var(--ink-soft))] border border-dashed border-[hsl(var(--rule))]">
        <AlertCircle size={16} className="inline mr-1" />
        Reports require Manager or Admin role.
      </div>
    );
  }
  return (
    <div data-testid="pi-reports" className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {REPORTS.map((r) => (
        <div key={r.kind} className="border border-[hsl(var(--rule))] p-4 flex items-center justify-between bg-[hsl(var(--bg-2))]">
          <div>
            <FileText size={14} className="inline mr-1 text-[hsl(var(--brass-2))]" />
            <span className="text-sm font-medium">{r.label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              data-testid={`pi-export-${r.kind}-xlsx`}
              onClick={() => onExport(r.kind, "xlsx")}
              disabled={exporting === `${r.kind}.xlsx`}
              className="text-xs px-3 py-1.5 border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg))] disabled:opacity-50"
            >
              {exporting === `${r.kind}.xlsx` ? "…" : "Excel"}
            </button>
            <button
              data-testid={`pi-export-${r.kind}-pdf`}
              onClick={() => onExport(r.kind, "pdf")}
              disabled={exporting === `${r.kind}.pdf`}
              className="text-xs px-3 py-1.5 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] disabled:opacity-50"
            >
              {exporting === `${r.kind}.pdf` ? "…" : "PDF"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
function ChartCard({ title, children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] ${className}`}>
      <div className="px-4 py-2 border-b border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
        <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">{title}</div>
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

function Empty({ msg }) {
  return <div className="text-xs text-[hsl(var(--ink-soft))] p-6 text-center border border-dashed border-[hsl(var(--rule))]">{msg}</div>;
}
